/**
 * escalationEngine — détection et traitement des tâches à escalader.
 *
 * Règles :
 *   Pour chaque tâche non terminée ayant une échéance passée depuis plus de N
 *   jours (N dépendant du quadrant et configuré dans settingsStore), on la
 *   marque comme "escalated" et on déclenche l'envoi d'un email vers le N+1.
 *
 *   Quadrant → délai d'escalade par défaut :
 *     Q1 (urgent + important)         → 2 jours
 *     Q2 (non urgent + important)     → 4 jours
 *     Q3 (urgent + non important)     → 3 jours
 *     Q4 (ni urgent ni important)     → 5 jours
 *
 *   L'escalade ne se déclenche qu'une fois par tâche : un tag 'escalated'
 *   est posé dans les tags de la tâche pour éviter les doublons.
 */
import type { Task, Quadrant } from '@store/types';
import type { EscalationDelays, Supervisor } from '@store/settingsStore';

export interface EscalationDecision {
  task: Task;
  quadrant: Quadrant;
  daysOverdue: number;
  delayThreshold: number;
  reason: string;
}

const MS_PER_DAY = 86_400_000;
const ESCALATED_TAG = 'escalated';

/**
 * Analyse une liste de tâches et retourne celles qui doivent être escaladées
 * maintenant (retard > seuil ET pas déjà escaladées).
 */
export function findTasksToEscalate(
  tasks: Task[],
  delays: EscalationDelays,
  now: Date = new Date(),
): EscalationDecision[] {
  const decisions: EscalationDecision[] = [];

  for (const task of tasks) {
    if (task.status === 'done' || task.status === 'deferred') continue;
    if (task.tags.includes(ESCALATED_TAG)) continue;
    if (!task.dueAt) continue;

    const due = new Date(task.dueAt).getTime();
    const overdueMs = now.getTime() - due;
    if (overdueMs <= 0) continue;

    const daysOverdue = overdueMs / MS_PER_DAY;
    const threshold = delays[task.quadrant];
    if (daysOverdue >= threshold) {
      decisions.push({
        task,
        quadrant: task.quadrant,
        daysOverdue: Math.round(daysOverdue * 10) / 10,
        delayThreshold: threshold,
        reason: `Retard de ${daysOverdue.toFixed(1)} jour(s) — seuil ${task.quadrant} = ${threshold}j`,
      });
    }
  }

  return decisions;
}

/**
 * Construit le corps d'un email d'escalade vers le N+1.
 * Format standard, en français, professionnel.
 */
export function buildEscalationEmail(
  decision: EscalationDecision,
  supervisor: Supervisor,
  senderName: string,
): { to: string; subject: string; body: string } {
  const { task, daysOverdue, quadrant } = decision;
  const subject = `[ESCALADE ${quadrant}] ${task.title}`;
  const body =
    `Bonjour ${supervisor.name || ''},\n\n` +
    `Conformément aux règles d'escalade paramétrées dans UNIQUE, ` +
    `je vous informe qu'une tâche sous ma responsabilité a dépassé ` +
    `son délai limite de traitement et nécessite votre attention.\n\n` +
    `• Tâche : ${task.title}\n` +
    (task.description ? `• Description : ${task.description}\n` : '') +
    `• Quadrant : ${quadrant}\n` +
    `• Échéance initiale : ${task.dueAt ? new Date(task.dueAt).toLocaleString('fr-FR') : '—'}\n` +
    `• Retard actuel : ${daysOverdue.toFixed(1)} jour(s)\n\n` +
    `Je reste à votre disposition pour échanger sur les actions à prendre.\n\n` +
    `Cordialement,\n${senderName}`;
  return { to: supervisor.email, subject, body };
}

/**
 * Renvoie le tag à ajouter à une tâche escaladée (pour éviter les doublons).
 */
export function escalatedTag(): string {
  return ESCALATED_TAG;
}

/**
 * Helper : détermine les mots-clés "urgent"/"important" présents dans un texte.
 * Utilisé à l'import d'emails pour ajuster automatiquement le quadrant.
 */
export function detectUrgencyFromText(
  text: string,
  urgentKeywords: string[],
  importantKeywords: string[],
): { urgent: boolean; important: boolean } {
  const lower = text.toLowerCase();
  const urgent = urgentKeywords.some((kw) => lower.includes(kw.toLowerCase()));
  const important = importantKeywords.some((kw) => lower.includes(kw.toLowerCase()));
  return { urgent, important };
}

/**
 * Helper : détermine le quadrant Eisenhower à partir des flags urgent/important.
 */
export function quadrantFromFlags(urgent: boolean, important: boolean): Quadrant {
  if (urgent && important) return 'Q1';
  if (!urgent && important) return 'Q2';
  if (urgent && !important) return 'Q3';
  return 'Q4';
}
